import React, { useMemo } from "react";
import { Box, Grid, Typography, Paper } from "@mui/material";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "../../services/mimshakService";
import { calculateRemainingMonths } from "../../utils/dateCalculators";
import { formatCurrency } from "../../utils/namesFormater";

interface Props {
  products: Product[];
}

const MetricCard = ({ label, value }: { label: string; value: string | number }) => (
  <Paper elevation={3} sx={{ p: 3, textAlign: "center", borderRadius: 2 }}>
    <Typography variant="h6" gutterBottom>{label}</Typography>
    <Typography variant="h5" fontWeight="bold">{value}</Typography>
  </Paper>
);

const LoanMetricsCards: React.FC<Props> = ({ products }) => {
  const {
    totalLoanAmount,
    totalLoanBalance,
    maxMonthsRemaining,
    totalMonthlyRepayment
  } = useMemo(() => {
    const loansWithPolicy = MimshakService.getDistinctLoansWithPolicy(products);

    let maxMonths = 0;
    let totalMonthly = 0;
    let monthlyRepayment = 0;

    loansWithPolicy.forEach(({ loan, policy }) => {
      const updateDate = policy.update_date || "";
      const monthsLeft = loan.loan_end_date
        ? calculateRemainingMonths(updateDate, loan.loan_end_date)
        : 0;

      if (monthsLeft > maxMonths) maxMonths = monthsLeft;

      monthlyRepayment += loan.periodic_repayment_amount || 0;

      if (loan.loan_balance && monthsLeft > 0) {
        totalMonthly += loan.loan_balance / monthsLeft;
      }
    });

    const totalLoanAmount = loansWithPolicy.reduce(
      (sum, { loan }) => sum + (loan.loan_amount || 0),
      0
    );

    const totalLoanBalance = loansWithPolicy.reduce(
      (sum, { loan }) => sum + (loan.loan_balance || 0),
      0
    );

    return {
      totalLoanAmount,
      totalLoanBalance,
      maxMonthsRemaining: maxMonths,
      totalMonthlyRepayment: monthlyRepayment,
    };
  }, [products]);

  const hasLoans = totalLoanAmount > 0;

  if (!hasLoans) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <MetricCard label="סך הלוואות שנלקחו" value={formatCurrency(totalLoanAmount)} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard label="סך יתרה להחזר" value={formatCurrency(totalLoanBalance)} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard label="חודשים מקסימליים לסיום" value={maxMonthsRemaining} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard label="סך החזר חודשי מוערך" value={formatCurrency(totalMonthlyRepayment)} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoanMetricsCards;
